import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  TrendingDown, 
  LogOut,
  ChevronRight,
  ArrowRight,
  CheckCircle,
  XCircle,
  Trash2,
  Zap,
  Flame,
  BarChart3
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ad8cae97-e844-4242-bf8a-3cc50b57fa19/artifacts/vj8eyvuz_logo%20%2B%20personaggio%20-%208energy%20srl%20%20%281%29.svg";
const OTTO_URL = "https://static.wixstatic.com/media/709795_dcb4777a39394c9fb86ba6ad9678de09~mv2.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (!user) {
        const userRes = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
        }
      }

      const statsRes = await fetch(`${API}/stats/user`, { credentials: "include" });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      const analysesRes = await fetch(`${API}/bills/analyses`, { credentials: "include" });
      if (analysesRes.ok) {
        const analysesData = await analysesRes.json();
        setAnalyses(analysesData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { 
        method: "POST", 
        credentials: "include" 
      });
      toast.success("Logout effettuato");
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleDeleteAnalysis = async (analysisId) => {
    if (!confirm("Sei sicuro di voler eliminare questa analisi?")) return;
    
    try {
      const response = await fetch(`${API}/bills/analyses/${analysisId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Analisi eliminata");
        setAnalyses(analyses.filter(a => a.analysis_id !== analysisId));
        fetchData();
      }
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-3 border-[#006838] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard">
              <img src={LOGO_URL} alt="8energy" className="h-8" />
            </Link>
            
            <div className="flex items-center gap-3">
              <Link to="/upload">
                <Button 
                  className="rounded-full bg-[#006838] hover:bg-[#005530] text-white h-10 px-5 font-medium"
                  data-testid="nav-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Carica bolletta
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="icon"
                className="rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Welcome */}
        <motion.div 
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ciao, {user?.name?.split(" ")[0] || "Utente"}
          </h1>
          <p className="text-gray-500">
            Ecco il riepilogo delle tue bollette analizzate
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Total */}
          <div className="bg-[#f5f5f7] rounded-3xl p-6" data-testid="stat-total">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-sm">
              <FileText className="w-6 h-6 text-gray-600" strokeWidth={1.5} />
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats?.totale_analisi || 0}</p>
            <p className="text-sm text-gray-500">bollette analizzate</p>
          </div>

          {/* Passed */}
          <div className="bg-[#f5f5f7] rounded-3xl p-6" data-testid="stat-passed">
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-green-600" strokeWidth={1.5} />
            </div>
            <p className="text-3xl font-bold text-green-600">{stats?.promosse || 0}</p>
            <p className="text-sm text-gray-500">promosse</p>
          </div>

          {/* Failed */}
          <div className="bg-[#f5f5f7] rounded-3xl p-6" data-testid="stat-failed">
            <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
              <XCircle className="w-6 h-6 text-red-500" strokeWidth={1.5} />
            </div>
            <p className="text-3xl font-bold text-red-500">{stats?.bocciate || 0}</p>
            <p className="text-sm text-gray-500">bocciate</p>
          </div>

          {/* Savings */}
          <div className="bg-gradient-to-br from-[#ADFF2F]/20 to-[#006838]/10 rounded-3xl p-6 border border-[#ADFF2F]/30" data-testid="stat-savings">
            <div className="w-12 h-12 bg-[#006838] rounded-2xl flex items-center justify-center mb-4">
              <TrendingDown className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <p className="text-3xl font-bold text-[#006838]">€{(stats?.risparmio_potenziale_totale || 0).toFixed(0)}</p>
            <p className="text-sm text-[#006838]/70">risparmio/mese</p>
          </div>
        </motion.div>

        {/* Quick Action - Upload */}
        <motion.div 
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Link to="/upload" data-testid="upload-card">
            <div className="relative bg-gradient-to-r from-[#006838] to-[#004d29] rounded-[2rem] p-8 border border-[#006838]/20 hover:shadow-2xl hover:shadow-[#006838]/20 transition-all group overflow-hidden">
              {/* Animated glow effect */}
              <div className="absolute inset-0 rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity" style={{
                background: 'radial-gradient(circle at 70% 50%, rgba(173,255,47,0.15), transparent 60%)',
              }}></div>
              
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                    <Upload className="w-8 h-8 text-[#ADFF2F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-extrabold text-white tracking-tight">Carica una bolletta</h3>
                    <p className="text-[#ADFF2F]/90 font-medium text-base mt-1">Scopri quanto puoi risparmiare in 60 secondi</p>
                  </div>
                </div>
                <div className="w-14 h-14 bg-[#ADFF2F] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-[#ADFF2F]/30">
                  <ArrowRight className="w-7 h-7 text-[#006838]" strokeWidth={2} />
                </div>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Recent Analyses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Analisi recenti
          </h2>

          {analyses.length === 0 ? (
            <div className="bg-[#f5f5f7] rounded-[2rem] p-12 text-center relative overflow-hidden">
              <motion.img 
                src={OTTO_URL} 
                alt="OTTO" 
                className="w-32 mx-auto mb-6"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ciao! Sono OTTO</h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                Carica la tua prima bolletta e ti aiuterò a scoprire se stai pagando il giusto prezzo!
              </p>
              <Link to="/upload">
                <Button 
                  className="rounded-full bg-[#006838] hover:bg-[#005530] text-white h-12 px-8 font-medium shadow-lg shadow-[#006838]/20"
                  data-testid="empty-upload-btn"
                >
                  <Upload className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Carica bolletta
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {analyses.map((analysis, index) => (
                <motion.div
                  key={analysis.analysis_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div 
                    className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group"
                    data-testid={`analysis-card-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                          analysis.bill_data?.tipo === "luce" 
                            ? "bg-amber-50" 
                            : "bg-orange-50"
                        }`}>
                          {analysis.bill_data?.tipo === "luce" ? (
                            <Zap className="w-7 h-7 text-amber-500" strokeWidth={1.5} />
                          ) : (
                            <Flame className="w-7 h-7 text-orange-500" strokeWidth={1.5} />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Bolletta {analysis.bill_data?.tipo === "luce" ? "Luce" : "Gas"}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {analysis.bill_data?.fornitore} • {analysis.bill_data?.periodo_fatturazione}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="font-bold text-gray-900 text-lg">€{analysis.bill_data?.costo_totale?.toFixed(2)}</p>
                          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                            analysis.rating === "promossa" 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-600"
                          }`}>
                            {analysis.rating === "promossa" ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5" />
                            )}
                            {analysis.rating === "promossa" ? "Promossa" : "Bocciata"}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Link to={`/analysis/${analysis.analysis_id}`}>
                            <button className="w-10 h-10 rounded-full bg-[#f5f5f7] hover:bg-[#006838] flex items-center justify-center transition-colors group/btn">
                              <ChevronRight className="w-5 h-5 text-gray-400 group-hover/btn:text-white" strokeWidth={1.5} />
                            </button>
                          </Link>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAnalysis(analysis.analysis_id);
                            }}
                            className="w-10 h-10 rounded-full hover:bg-red-50 flex items-center justify-center transition-colors"
                            data-testid={`delete-analysis-${index}`}
                          >
                            <Trash2 className="w-5 h-5 text-gray-300 hover:text-red-500" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}
